import React, { useEffect, startTransition } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from '@/contexts/auth/AuthContext';
import { initializeRouteOptimizer } from '@/utils/routeOptimizer';
import { initializePerformanceSystem } from '@/utils/masterPerformanceSystem';
import MainLayout from '@/layouts/MainLayout';
import { RouteGuard } from '@/components/guards/RouteGuard';
import AppErrorBoundary from '@/components/error/AppErrorBoundary';
import RouteErrorBoundary from '@/components/error/RouteErrorBoundary';
import { LazyRoute } from '@/components/optimized/LazyRoute';

// Lazy load route components for optimal bundle splitting
import {
  LazyLoginPage,
  LazyCompleteProfilePage,
  LazyForgotPasswordPage,
  LazyResetPasswordPage,
  LazyPendingReviewPage,
  LazyProfileRejectedPage,
  LazyAccessRequestPage,
  LazyIndexPage,
  LazyProfilePage,
  LazyNotFoundPage,
  LazyDebugPage,
  LazyMarketplaceBrowse,
  LazyMarketplaceUltraFastBrowse,
  LazyMarketplaceDetail,
  LazyMarketplaceSettings,
  LazyProductDetails,
  LazyOrderManagement,
  LazyAdminDashboard,
  LazyAdminUsers,
  LazyAdminCultivars,
  LazyAdminBatches,
  LazyAdminHardware,
  LazyAdminProducts,
  LazyAdminOrders
} from '@/components/lazy/LazyRoutes';

// Optimized Query Client with better defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true
    },
    mutations: {
      retry: 1
    }
  }
});

const OptimizedApp: React.FC = () => {
  useEffect(() => {
    // Initialize performance optimizations using startTransition for non-urgent updates
    startTransition(() => {
      initializePerformanceSystem();
      initializeRouteOptimizer();
    });
    
    // Cleanup on unmount
    return () => {
      // Performance system handles its own cleanup
    };
  }, []);

  return (
    <AppErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <QueryClientProvider client={queryClient}>
          <Router>
            <AuthProvider>
              <RouteErrorBoundary>
                <Routes>
                  {/* Public Authentication Routes */}
                  <Route path="/auth/login" element={
                    <LazyRoute component={LazyLoginPage} name="Login" />
                  } />
                  <Route path="/auth/complete-profile" element={
                    <LazyRoute component={LazyCompleteProfilePage} name="Complete Profile" />
                  } />
                  <Route path="/auth/forgot-password" element={
                    <LazyRoute component={LazyForgotPasswordPage} name="Forgot Password" />
                  } />
                  <Route path="/auth/reset-password" element={
                    <LazyRoute component={LazyResetPasswordPage} name="Reset Password" />
                  } />
                  <Route path="/auth/pending-review" element={
                    <LazyRoute component={LazyPendingReviewPage} name="Pending Review" />
                  } />
                  <Route path="/auth/profile-rejected" element={
                    <LazyRoute component={LazyProfileRejectedPage} name="Profile Rejected" />
                  } />
                  <Route path="/access-request" element={
                    <LazyRoute component={LazyAccessRequestPage} name="Access Request" />
                  } />
                  
                  {/* Protected Routes with Layout */}
                  <Route path="/" element={
                    <RouteGuard>
                      <MainLayout />
                    </RouteGuard>
                  }>
                    {/* Main Application Routes */}
                    <Route index element={
                      <LazyRoute component={LazyMarketplaceUltraFastBrowse} name="Marketplace" />
                    } />
                    <Route path="profile" element={
                      <LazyRoute component={LazyProfilePage} name="Profile" />
                    } />
                    <Route path="debug" element={
                      <LazyRoute component={LazyDebugPage} name="Debug" />
                    } />
                    
                    {/* Marketplace Routes - Direct to Browse */}
                    <Route path="marketplace" element={
                      <LazyRoute component={LazyMarketplaceUltraFastBrowse} name="Marketplace" />
                    } />
                    
                    {/* Marketplace Settings - Top Level Route */}
                    <Route path="marketplace-settings" element={
                      <LazyRoute component={LazyMarketplaceSettings} name="Marketplace Settings" />
                    } />
                    <Route path="marketplace/:entityType/:id" element={
                      <LazyRoute component={LazyMarketplaceDetail} name="Marketplace Detail" />
                    } />
                    <Route path="product/:id" element={
                      <LazyRoute component={LazyProductDetails} name="Product Details" />
                    } />
                    <Route path="orders" element={
                      <LazyRoute component={LazyOrderManagement} name="Order Management" />
                    } />
                    
                    {/* Admin Routes */}
                    <Route path="dashboard" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminDashboard} name="Admin Dashboard" />
                      </RouteGuard>
                    } />
                    <Route path="admin/dashboard" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminDashboard} name="Admin Dashboard" />
                      </RouteGuard>
                    } />
                    <Route path="admin/users" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminUsers} name="Admin Users" />
                      </RouteGuard>
                    } />
                    <Route path="admin/cultivars" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminCultivars} name="Admin Cultivars" />
                      </RouteGuard>
                    } />
                    <Route path="admin/batches" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminBatches} name="Admin Batches" />
                      </RouteGuard>
                    } />
                    <Route path="admin/hardware" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminHardware} name="Admin Hardware" />
                      </RouteGuard>
                    } />
                    <Route path="admin/products" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminProducts} name="Admin Products" />
                      </RouteGuard>
                    } />
                    <Route path="admin/orders" element={
                      <RouteGuard requireAdmin={true}>
                        <LazyRoute component={LazyAdminOrders} name="Admin Orders" />
                      </RouteGuard>
                    } />
                    
                    {/* 404 Fallback */}
                    <Route path="*" element={
                      <LazyRoute component={LazyNotFoundPage} name="Not Found" />
                    } />
                  </Route>
                </Routes>
              </RouteErrorBoundary>
              
              {/* Global Toast Notifications */}
              <Toaster 
                position="bottom-right"
                toastOptions={{
                  duration: 4000,
                  className: 'bg-background border border-border'
                }}
              />
            </AuthProvider>
          </Router>
        </QueryClientProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
};

export default OptimizedApp;